import { zodResolver } from '@hookform/resolvers/zod'
import { CreditCard, Landmark, Loader2 } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import { useSaveAccount } from '../api'
import { bankByIban } from '../banks'
import { CURRENCIES, currencySuffix, type Currency } from '../fx'
import { isValidTrIban } from '../iban'
import { ACCOUNT_TYPES, CARD_BRANDS, SCOPE_ACCOUNT_TYPES, type AccountScope, type FinAccount } from '../types'
import { toastError, useFinanceLookups } from '../ui'
import { ACCOUNT_ICON } from '../visuals'
import { amountRule } from '../validation'
import { BankSelect } from './banks/BankSelect'
import { CheckRow, Field, FormSection } from './FormBits'

export function Footer({
  pending,
  disabled,
  onCancel,
}: {
  pending: boolean
  disabled?: boolean
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <Button type="button" variant="cancel" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={pending || disabled}>
        {pending ? <Loader2 className="animate-spin" /> : null}
        {t('common.save')}
      </Button>
    </>
  )
}

type AccountFormProps = { account: FinAccount | null; scope: AccountScope; onDone: () => void }

export function AccountForm({ account, scope, onDone }: AccountFormProps) {
  const { t } = useTranslation()
  // Vadeli hesap formdan değil Vadeli Hesaplar sayfasından açılır (anapara aktarımı ve faiz koşullarıyla).
  const types = SCOPE_ACCOUNT_TYPES[scope].filter((x) => x !== 'deposit' || account?.type === 'deposit')
  const isCards = scope === 'cards'
  const save = useSaveAccount()
  const day = z.string().regex(/^([1-9]|1\d|2[0-8])?$/, t('finance.accounts.form.dayInvalid'))
  const schema = z
    .object({
      name: z.string().trim().min(2, t('finance.accounts.form.nameRequired')).max(60),
      type: z.enum(['', ...ACCOUNT_TYPES]).refine((v) => v !== '', t('finance.accounts.form.typeRequired')),
      bankName: z.string().trim().max(60),
      bankId: z.string(),
      branch: z.string().trim().max(60),
      accountNo: z.string().trim().max(30),
      iban: z.string().refine((v) => !v.trim() || isValidTrIban(v), t('errors.IBAN_INVALID')),
      clearIban: z.boolean(),
      merchantId: z.string().trim().max(30),
      cardBrand: z.enum(['', ...CARD_BRANDS]),
      cardHolder: z.string().trim().max(60),
      cardLast4: z.string().trim(),
      cardExpiry: z.string().trim(),
      cardLimit: amountRule(t('finance.form.amountInvalid')),
      cashAdvanceLimit: amountRule(t('finance.form.amountInvalid')),
      statementDay: day,
      dueDay: day,
      openingBalance: amountRule(t('finance.form.amountInvalid')),
      currency: z.enum(CURRENCIES),
      active: z.boolean(),
      settlementMode: z.enum(['manual', 'auto']),
      settlementDays: z.string().regex(/^(\d|[1-5]\d|60)?$/, t('finance.accounts.pos.daysInvalid')),
      commissionRate: z
        .string()
        .trim()
        .regex(/^(\d{1,2}([.,]\d{1,4})?)?$/, t('finance.accounts.pos.rateInvalid')),
      settlementAccountId: z.string(),
    })
    .superRefine((v, ctx) => {
      // Otomatik POS tahsilatı valör günü, komisyon oranı ve hedef kasa/banka olmadan çalışamaz.
      if (v.type === 'pos' && v.settlementMode === 'auto') {
        if (!v.settlementDays)
          ctx.addIssue({ code: 'custom', path: ['settlementDays'], message: t('finance.accounts.pos.daysRequired') })
        if (!v.commissionRate)
          ctx.addIssue({ code: 'custom', path: ['commissionRate'], message: t('finance.accounts.pos.rateRequired') })
        if (!v.settlementAccountId)
          ctx.addIssue({
            code: 'custom',
            path: ['settlementAccountId'],
            message: t('finance.accounts.pos.targetRequired'),
          })
      }
      if (v.type !== 'credit_card') return
      if (!/^\d{4}$/.test(v.cardLast4))
        ctx.addIssue({ code: 'custom', path: ['cardLast4'], message: t('errors.CARD_LAST4_INVALID') })
      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(v.cardExpiry))
        ctx.addIssue({ code: 'custom', path: ['cardExpiry'], message: t('errors.CARD_EXPIRY_INVALID') })
    })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: account?.name ?? '',
      type: account?.type ?? (types.length === 1 && types[0] ? types[0] : ('' as const)),
      bankName: account?.bankName ?? '',
      bankId: account?.bankId ?? '',
      branch: account?.branch ?? '',
      accountNo: account?.accountNo ?? '',
      iban: '',
      clearIban: false,
      merchantId: account?.merchantId ?? '',
      cardBrand: account?.cardBrand ?? '',
      cardHolder: account?.cardHolder ?? '',
      cardLast4: account?.cardLast4 ?? '',
      cardExpiry: account?.cardExpiry ?? '',
      cardLimit: account?.cardLimit ?? '0.00',
      cashAdvanceLimit: account?.cashAdvanceLimit ?? '0.00',
      statementDay: account?.statementDay ? String(account.statementDay) : '',
      dueDay: account?.dueDay ? String(account.dueDay) : '',
      openingBalance: account?.openingBalance ?? '0.00',
      currency: (account?.currency ?? 'TRY') as Currency,
      active: account?.active ?? true,
      settlementMode: account?.settlementMode ?? 'manual',
      settlementDays: account?.settlementDays != null ? String(account.settlementDays) : '1',
      commissionRate: account?.commissionRate ?? '',
      settlementAccountId: account?.settlementAccountId ?? '',
    },
  })
  const { accounts } = useFinanceLookups()
  const settlementTargets = accounts.filter((a) => a.active && (a.type === 'cash' || a.type === 'bank'))
  const { errors } = form.formState
  const type = useWatch({ control: form.control, name: 'type' })
  const currency = useWatch({ control: form.control, name: 'currency' })
  const settlementMode = useWatch({ control: form.control, name: 'settlementMode' })
  const hasIban = type === 'bank' || type === 'pos'

  const onSubmit = form.handleSubmit(async (v) => {
    const { type } = v
    if (!type) return
    try {
      await save.mutateAsync({
        id: account?.id,
        input: {
          ...v,
          type,
          statementDay: v.statementDay ? Number(v.statementDay) : null,
          dueDay: v.dueDay ? Number(v.dueDay) : null,
          settlementDays: v.settlementDays ? Number(v.settlementDays) : null,
          commissionRate: v.commissionRate.replace(',', '.'),
          currency: type === 'cash' || type === 'bank' || type === 'deposit' ? v.currency : 'TRY',
        },
      })
      toast.success(t(account ? 'finance.accounts.updated' : 'finance.accounts.created'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'ACCOUNT_NAME_TAKEN')
        form.setError('name', { message: t('errors.ACCOUNT_NAME_TAKEN') })
      else if (isApiError(e) && e.code === 'IBAN_INVALID') form.setError('iban', { message: t('errors.IBAN_INVALID') })
      else toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={isCards ? <CreditCard /> : <Landmark />}
      title={t(
        account
          ? account.type === 'credit_card'
            ? 'finance.cards.editTitle'
            : 'finance.accounts.form.editTitle'
          : isCards
            ? 'finance.cards.createTitle'
            : 'finance.accounts.form.createTitle',
      )}
      description={t(isCards ? 'finance.cards.formHint' : 'finance.accounts.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={<Footer pending={save.isPending} disabled={!type} onCancel={onDone} />}
    >
      <div className="space-y-6">
        {types.length > 1 ? (
          <fieldset className="min-w-0 space-y-3">
            <legend className="text-muted-foreground mb-3 w-full border-b pb-1.5 text-xs font-semibold tracking-wide uppercase">
              {t('finance.accounts.form.sectionType')}
            </legend>
            <Controller
              control={form.control}
              name="type"
              render={({ field }) => (
                <div
                  role="radiogroup"
                  aria-label={t('finance.accounts.type')}
                  className={cn('grid grid-cols-2 gap-2', types.length > 2 && 'sm:grid-cols-4')}
                >
                  {types.map((x) => {
                    const Icon = ACCOUNT_ICON[x]
                    const selected = field.value === x
                    return (
                      <button
                        key={x}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={!!account && !selected}
                        onClick={() => {
                          field.onChange(x)
                          if (!account) requestAnimationFrame(() => form.setFocus('name'))
                        }}
                        className={cn(
                          'focus-visible:ring-ring flex flex-col items-start gap-2 rounded-lg border p-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40',
                          selected ? 'border-primary bg-primary/5 ring-primary ring-1' : 'hover:bg-muted/50',
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-8 items-center justify-center rounded-md',
                            selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                          )}
                        >
                          <Icon className="size-4" aria-hidden />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">{t(`finance.accountType.${x}`)}</span>
                          <span className="text-muted-foreground block text-xs">
                            {t(`finance.accounts.form.typeHint.${x}`)}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            />
          </fieldset>
        ) : null}

        {!type ? (
          <p className="bg-muted/20 text-muted-foreground rounded-lg border border-dashed p-4 text-center text-sm">
            {t('finance.accounts.form.typePrompt')}
          </p>
        ) : (
          <>
            <FormSection title={t('finance.accounts.form.sectionGeneral')}>
              <Field id="ac-name" label={t('finance.accounts.name')} error={errors.name?.message}>
                <Input id="ac-name" aria-invalid={!!errors.name} {...form.register('name')} />
              </Field>
              {type === 'cash' || type === 'bank' || type === 'deposit' ? (
                <Field id="ac-currency" label={t('finance.fx.currency')} hint={t(account ? 'finance.fx.currencyLocked' : 'finance.fx.currencyHint')}>
                  <FormSelect
                    id="ac-currency"
                    control={form.control}
                    name="currency"
                    disabled={!!account}
                    options={CURRENCIES.map((c) => ({ value: c, label: t(`finance.fx.currencies.${c}`) }))}
                  />
                </Field>
              ) : null}
              <Field id="ac-open" label={t('finance.accounts.opening')} error={errors.openingBalance?.message}>
                <Controller
                  control={form.control}
                  name="openingBalance"
                  render={({ field }) => (
                    <NumberInput
                      id="ac-open"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      minDecimalsOnBlur={2}
                      suffix={currencySuffix(currency)}
                    />
                  )}
                />
              </Field>
            </FormSection>

            {hasIban ? (
              <FormSection title={t('finance.accounts.form.sectionBank')}>
                <Field id="ac-bank" label={t('finance.accounts.bank')} hint={t('finance.banks.fromIbanHint')}>
                  <Controller
                    control={form.control}
                    name="bankId"
                    render={({ field }) => <BankSelect id="ac-bank" value={field.value} onChange={field.onChange} />}
                  />
                </Field>
                <Field id="ac-branch" label={t('finance.accounts.branch')}>
                  <Input
                    id="ac-branch"
                    placeholder={t('finance.accounts.form.branchPlaceholder')}
                    {...form.register('branch')}
                  />
                </Field>
                <Field
                  id="ac-no"
                  label={type === 'pos' ? t('finance.accounts.merchantId') : t('finance.accounts.accountNo')}
                >
                  {type === 'pos' ? (
                    <Input id="ac-no" {...form.register('merchantId')} />
                  ) : (
                    <Input id="ac-no" {...form.register('accountNo')} />
                  )}
                </Field>
                <Field
                  id="ac-iban"
                  label={t(type === 'pos' ? 'finance.accounts.settlementIban' : 'finance.accounts.iban')}
                  error={errors.iban?.message}
                  hint={
                    account?.ibanMasked
                      ? t('finance.accounts.form.ibanKeep', { masked: account.ibanMasked })
                      : t('finance.accounts.form.ibanHint')
                  }
                  full
                >
                  <Input
                    id="ac-iban"
                    className="font-mono uppercase"
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    autoComplete="off"
                    aria-invalid={!!errors.iban}
                    {...form.register('iban', {
                      // IBAN'daki banka kodundan banka seçilir (seçim yoksa).
                      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                        if (form.getValues('bankId') || e.target.value.replace(/\s/g, '').length < 9) return
                        void bankByIban(e.target.value).then((b) => b && form.setValue('bankId', b.id, { shouldDirty: true }))
                      },
                    })}
                  />
                </Field>
                {account?.ibanMasked ? (
                  <CheckRow {...form.register('clearIban')}>{t('finance.accounts.form.clearIban')}</CheckRow>
                ) : null}
                {type === 'bank' ? (
                  <Field
                    id="ac-ca-limit"
                    label={t('finance.cashAdvances.accountLimit')}
                    hint={t('finance.cashAdvances.accountLimitHint')}
                    error={errors.cashAdvanceLimit?.message}
                  >
                    <Controller
                      control={form.control}
                      name="cashAdvanceLimit"
                      render={({ field }) => (
                        <NumberInput
                          id="ac-ca-limit"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          minDecimalsOnBlur={2}
                          suffix="₺"
                        />
                      )}
                    />
                  </Field>
                ) : null}
              </FormSection>
            ) : null}

            {type === 'pos' ? (
              <FormSection title={t('finance.accounts.pos.section')}>
                <Field
                  id="ac-pos-mode"
                  label={t('finance.accounts.pos.mode')}
                  hint={t(`finance.accounts.pos.modeHint.${settlementMode}`)}
                  full
                >
                  <FormSelect
                    id="ac-pos-mode"
                    control={form.control}
                    name="settlementMode"
                    options={(['manual', 'auto'] as const).map((m) => ({
                      value: m,
                      label: t(`finance.accounts.pos.modes.${m}`),
                    }))}
                  />
                </Field>
                {settlementMode === 'auto' ? (
                  <>
                    <Field
                      id="ac-pos-days"
                      label={t('finance.accounts.pos.days')}
                      error={errors.settlementDays?.message}
                      hint={t('finance.accounts.pos.daysHint')}
                    >
                      <Input id="ac-pos-days" inputMode="numeric" maxLength={2} {...form.register('settlementDays')} />
                    </Field>
                    <Field
                      id="ac-pos-rate"
                      label={t('finance.accounts.pos.rate')}
                      error={errors.commissionRate?.message}
                    >
                      <Input
                        id="ac-pos-rate"
                        inputMode="decimal"
                        placeholder="1,79"
                        {...form.register('commissionRate')}
                      />
                    </Field>
                    <Field
                      id="ac-pos-target"
                      label={t('finance.accounts.pos.target')}
                      error={errors.settlementAccountId?.message}
                      full
                    >
                      <FormSelect
                        id="ac-pos-target"
                        control={form.control}
                        name="settlementAccountId"
                        placeholder={t('common.select')}
                        options={settlementTargets.map((a) => ({
                          value: a.id,
                          label: a.name,
                          description: t(`finance.accountType.${a.type}`),
                        }))}
                      />
                    </Field>
                  </>
                ) : null}
              </FormSection>
            ) : null}

            {type === 'credit_card' ? (
              <FormSection title={t('finance.accounts.form.sectionCard')}>
                <Field id="ac-bank" label={t('finance.accounts.bank')}>
                  <Controller
                    control={form.control}
                    name="bankId"
                    render={({ field }) => <BankSelect id="ac-bank" value={field.value} onChange={field.onChange} />}
                  />
                </Field>
                <Field id="ac-brand" label={t('finance.accounts.cardBrand')}>
                  <FormSelect
                    id="ac-brand"
                    control={form.control}
                    name="cardBrand"
                    options={[
                      { value: '' as const, label: t('common.select') },
                      ...CARD_BRANDS.map((b) => ({ value: b, label: t(`finance.cardBrand.${b}`) })),
                    ]}
                  />
                </Field>
                <Field id="ac-holder" label={t('finance.accounts.cardHolder')} full>
                  <Input id="ac-holder" className="uppercase" {...form.register('cardHolder')} />
                </Field>
                <Field
                  id="ac-last4"
                  label={t('finance.accounts.cardLast4')}
                  error={errors.cardLast4?.message}
                  hint={t('finance.accounts.form.cardSafety')}
                >
                  <Input
                    id="ac-last4"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="1234"
                    autoComplete="off"
                    {...form.register('cardLast4')}
                  />
                </Field>
                <Field id="ac-exp" label={t('finance.accounts.cardExpiry')} error={errors.cardExpiry?.message}>
                  <Input
                    id="ac-exp"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder={t('finance.accounts.form.expiryPlaceholder')}
                    autoComplete="off"
                    {...form.register('cardExpiry')}
                  />
                </Field>
                <Field id="ac-limit" label={t('finance.accounts.cardLimit')} error={errors.cardLimit?.message}>
                  <Controller
                    control={form.control}
                    name="cardLimit"
                    render={({ field }) => (
                      <NumberInput
                        id="ac-limit"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        minDecimalsOnBlur={2}
                        suffix="₺"
                      />
                    )}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field id="ac-stmt" label={t('finance.accounts.statementDay')} error={errors.statementDay?.message}>
                    <Input
                      id="ac-stmt"
                      inputMode="numeric"
                      maxLength={2}
                      placeholder="15"
                      {...form.register('statementDay')}
                    />
                  </Field>
                  <Field id="ac-due" label={t('finance.accounts.dueDay')} error={errors.dueDay?.message}>
                    <Input
                      id="ac-due"
                      inputMode="numeric"
                      maxLength={2}
                      placeholder="25"
                      {...form.register('dueDay')}
                    />
                  </Field>
                </div>
              </FormSection>
            ) : null}

            <CheckRow {...form.register('active')}>{t('finance.accounts.activeLabel')}</CheckRow>
          </>
        )}
      </div>
    </Modal>
  )
}
